"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommercetoolsAuth = void 0;
var __1 = require("../");
var CommercetoolsGrant_1 = require("./CommercetoolsGrant");
var CommercetoolsAuthApi_1 = require("./CommercetoolsAuthApi");
/**
 * Default values for a couple of the properties required by the {@see Config}
 * interface used in {@see CommercetoolsAuth.constructor}.
 */
var configDefaults = {
    refreshIfWithinSecs: 1800,
    timeoutMs: 5000
};
/**
 * Provides an easy to use set of methods for communicating with the commercetools
 * HTTP Authorization API: https://docs.commercetools.com/api/authorization
 *
 * To create an instance of the class and get a client grant:
 * ```typescript
 * import { Region, CommercetoolsAuth } from '@gradientedge/commercetools-utils'
 *
 * async function example() {
 *   const auth = new CommercetoolsAuth({
 *     projectKey: 'your-project-key',
 *     clientId: 'your-client-id',
 *     clientSecret: 'your-client-secret',
 *     region: Region.EUROPE_GCP,
 *     clientScopes: ['create_anonymous_token']
 *   })
 *
 *   const grant = await auth.getClientGrant()
 *
 *   console.log('Grant:', grant)
 * }
 *
 * example()
 * ```
 *
 * An instance of this class is designed to be stored as a global, long-lived
 * object. If you're using a serverless environment such as AWS Lambda or an
 * Azure Function App, you can safely instantiate this class outside of your
 * function handler and have it exist for as long the serverless environment
 * allows.
 */
var CommercetoolsAuth = /** @class */ (function () {
    /**
     * Store the configuration locally and do some light validation to ensure
     * that we have some `clientScopes` defined. We also set a `member` called
     * {@see api} to an instance of {@see CommercetoolsAuthApi}, which handles
     * the lower level commercetools request.
     */
    function CommercetoolsAuth(config) {
        /**
         * Whenever we either refresh the client grant, or request a new one,
         * we don't want to allow any other requests to be initiated until that
         * request has completed. This promise is used to determine whether incoming
         * requests need to wait on an existing client grant request to complete
         * before they can start to be processed.
         */
        this.grantPromise = Promise.resolve();
        this.config = __assign(__assign({}, configDefaults), config);
        if (!this.config.clientScopes.length) {
            throw new __1.CommercetoolsError('`config.clientScopes` must contain at least one scope');
        }
        this.api = new CommercetoolsAuthApi_1.CommercetoolsAuthApi(config);
    }
    /**
     * Get a client grant
     *
     * If we don't already have a client grant stored locally, then we make a
     * call to commercetools to generate one. Once we have a grant, we store it
     * locally and then return that cached version up until it needs to be renewed.
     */
    CommercetoolsAuth.prototype.getClientGrant = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0: return [4 /*yield*/, this.grantPromise];
                    case 1:
                        _c.sent();
                        if (this.grant && !this.grant.expiresWithin(this.config.refreshIfWithinSecs)) {
                            return [2 /*return*/, this.grant];
                        }
                        this.grantPromise = this.api.getClientGrant(this.config.clientScopes);
                        _a = this;
                        _b = CommercetoolsGrant_1.CommercetoolsGrant.bind;
                        return [4 /*yield*/, this.grantPromise];
                    case 2:
                        _a.grant = new (_b.apply(CommercetoolsGrant_1.CommercetoolsGrant, [void 0, _c.sent()]))();
                        return [2 /*return*/, this.grant];
                }
            });
        });
    };
    /**
     * Refresh the customer's grant given a refresh token
     */
    CommercetoolsAuth.prototype.refreshCustomerGrant = function (refreshToken) {
        return __awaiter(this, void 0, void 0, function () {
            var data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getClientGrant()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.api.refreshGrant(refreshToken)];
                    case 2:
                        data = _a.sent();
                        return [2 /*return*/, new CommercetoolsGrant_1.CommercetoolsGrant(__assign(__assign({}, data), { refresh_token: refreshToken }))];
                }
            });
        });
    };
    /**
     * Login the customer using the given options.
     *
     * If the {@link LoginOptions.scopes} property isn't set on the `options`
     * parameter then we'll use the scopes set on {@link CommercetoolsAuthConfig.customerScopes}.
     * If this doesn't contain any values either, then this method will throw
     * an error due to the security risk of the customer having a grant with
     * the same privileges as the client API key.
     *
     * Example login code:
     * ```typescript
     * import { Region, CommercetoolsAuth } from '@gradientedge/commercetools-utils'
     *
     * async function example() {
     *   const auth = new CommercetoolsAuth({
     *     projectKey: 'your-project-key',
     *     clientId: 'your-client-id',
     *     clientSecret: 'your-client-secret',
     *     region: Region.EUROPE_GCP,
     *     clientScopes: [
     *       'view_published_products',
     *       'view_categories',
     *       'manage_customers',
     *       'create_anonymous_token'
     *     ]
     *   })
     *
     *   const grant = await auth.login({
     *     username: 'test@gradientedge.com',
     *     password: 'testing123'
     *   })
     *
     *   console.log('Customer grant:', grant)
     * }
     *
     * example()
     * ```
     *
     * Note that there is no need to call {@link getClientGrant} in order to
     * generate a client grant. The class internally requests one if it
     * doesn't have a non-expired grant already cached locally.
     */
    CommercetoolsAuth.prototype.login = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var scopes, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        scopes = options.scopes || this.config.customerScopes;
                        if (!scopes) {
                            throw new __1.CommercetoolsError('Customer scopes must be set on either the `options` ' +
                                'parameter of this `login` method, or on the `customerScopes` ' +
                                'property of the `CommercetoolsAuth` constructor');
                        }
                        return [4 /*yield*/, this.getClientGrant()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.api.login(__assign(__assign({}, options), { scopes: scopes, projectKey: this.config.projectKey }))];
                    case 2:
                        data = _a.sent();
                        return [2 /*return*/, new CommercetoolsGrant_1.CommercetoolsGrant(data)];
                }
            });
        });
    };
    /**
     * Get a grant for an anonymous customer.
     *
     * If you pass a value to the {@link AnonymousGrantOptions.anonymousId}
     * `options` parameter property then this must not exist within the commercetools
     * system for your project key, otherwise an error will be thrown. Typically
     * this property would be left undefined and commercetools will then create
     * a unique id for the anonymous user automatically.
     *
     * Example code to generate an anonymous customer grant:
     * ```typescript
     * import { Region, CommercetoolsAuth } from '@gradientedge/commercetools-utils'
     *
     * async function example() {
     *   const auth = new CommercetoolsAuth({
     *     projectKey: 'your-project-key',
     *     clientId: 'your-client-id',
     *     clientSecret: 'your-client-secret',
     *     region: Region.EUROPE_GCP,
     *     clientScopes: [
     *       'view_published_products',
     *       'view_categories',
     *       'manage_customers',
     *       'create_anonymous_token'
     *     ]
     *   })
     *
     *   const grant = await auth.getAnonymousGrant()
     *
     *   console.log('Anonymous customer grant:', grant)
     * }
     *
     * example()
     * ```
     */
    CommercetoolsAuth.prototype.getAnonymousGrant = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var scopes, anonymousId, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        scopes = (options === null || options === void 0 ? void 0 : options.scopes) || this.config.customerScopes;
                        anonymousId = options === null || options === void 0 ? void 0 : options.anonymousId;
                        if (!scopes) {
                            throw new __1.CommercetoolsError('Customer scopes must be set on either the `options` ' +
                                'parameter of this `login` method, or on the `customerScopes` ' +
                                'property of the `CommercetoolsAuth` constructor');
                        }
                        return [4 /*yield*/, this.getClientGrant()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.api.getAnonymousGrant({
                                scopes: scopes,
                                anonymousId: anonymousId
                            })];
                    case 2:
                        data = _a.sent();
                        return [2 /*return*/, new CommercetoolsGrant_1.CommercetoolsGrant(data)];
                }
            });
        });
    };
    return CommercetoolsAuth;
}());
exports.CommercetoolsAuth = CommercetoolsAuth;
//# sourceMappingURL=CommercetoolsAuth.js.map